import { Card, Checkbox, Input, Tabs, TabsProps, Tag } from 'antd';
import React from 'react';

export interface SelectableItem {
  label: string;
  value: string;
}

export interface CustomSelectorTabInterface<T> {
  label: string;
  selected: T[];
  setSelected: (selected: T[]) => void;
  fetchSearch: (search: string) => Promise<T[]>;
  getLabel: (item: T) => string;
}

export interface CustomSelectorProps<T> {
  tabs: CustomSelectorTabInterface<T>[];
}

const CustomSelector = <T extends string | number | object>({ tabs }: CustomSelectorProps<T>) => {
  const [selectedTab, setSelectedTab] = React.useState<string>(tabs[0]?.label || '');
  const [tabStates, setTabStates] = React.useState<
    Record<string, { searchKeyword: string; options: T[] }>
  >({});

  const updateTabState = (
    tabLabel: string,
    updates: Partial<{ searchKeyword: string; options: T[] }>
  ) => {
    setTabStates(prev => ({
      ...prev,
      [tabLabel]: { ...prev[tabLabel], ...updates },
    }));
  };

  // 선택된 항목들을 추적하기 위한 상태 (내부 상태로 완전히 관리)
  const [selectedItems, setSelectedItems] = React.useState<Record<string, T[]>>({});
  const initializedRef = React.useRef<Set<string>>(new Set());

  // 각 탭별로 초기화 및 업데이트
  React.useEffect(() => {
    tabs.forEach(tab => {
      if (!initializedRef.current.has(tab.label)) {
        // 처음 초기화할 때만
        setSelectedItems(prev => {
          if (prev[tab.label] === undefined) {
            return {
              ...prev,
              [tab.label]: [...tab.selected],
            };
          }
          return prev;
        });
        initializedRef.current.add(tab.label);
      } else {
        // 이미 초기화된 경우, selected 값이 변경되었는지 확인하고 업데이트
        setSelectedItems(prev => {
          const currentSelected = prev[tab.label] || [];
          const newSelected = tab.selected || [];

          // 선택된 항목이 변경되었는지 확인 (길이 또는 내용 비교)
          const hasChanged =
            currentSelected.length !== newSelected.length ||
            currentSelected.some(
              (item, index) => tab.getLabel(item) !== tab.getLabel(newSelected[index])
            ) ||
            newSelected.some(
              (item, index) => tab.getLabel(item) !== tab.getLabel(currentSelected[index])
            );

          if (hasChanged) {
            return {
              ...prev,
              [tab.label]: [...newSelected],
            };
          }
          return prev;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  const tabItems: TabsProps['items'] = React.useMemo(() => {
    return tabs.map(tab => {
      const tabState = tabStates[tab.label] || { searchKeyword: '', options: [] };
      // selectedItems가 있으면 사용하고, 없으면 tab.selected 사용 (초기 로드 시)
      const currentSelected =
        selectedItems[tab.label] !== undefined ? selectedItems[tab.label] : tab.selected;

      // 선택된 항목들을 options에 포함시키기 (이미 선택된 항목이 options에 없을 수 있음)
      const allOptions = [...tabState.options];
      currentSelected.forEach(selectedItem => {
        const label = tab.getLabel(selectedItem);
        const exists = allOptions.some(item => tab.getLabel(item) === label);
        if (!exists) {
          allOptions.push(selectedItem);
        }
      });

      return {
        key: tab.label,
        label: tab.label,
        children: (
          <div style={{ display: 'flex', flexDirection: 'row', height: '150px', gap: 5 }}>
            <Card
              style={{
                width: '20%',
                height: '150px',
                border: '1px solid black',
                overflowY: 'auto',
              }}
            >
              {currentSelected.length > 0 ? (
                currentSelected.map((item, index) => (
                  <Tag
                    key={`${tab.getLabel(item)}-${index}`}
                    style={{ marginBottom: '4px', display: 'block' }}
                  >
                    {tab.getLabel(item)}
                  </Tag>
                ))
              ) : (
                <div style={{ color: '#999', fontSize: '12px' }}>No items selected</div>
              )}
            </Card>
            <div style={{ width: '100%', height: '100%', border: '1px solid black', padding: 10 }}>
              <div style={{ height: '20%', marginBottom: 10 }}>
                <Input
                  placeholder={`Search ${tab.label}`}
                  value={tabState.searchKeyword}
                  onChange={e => updateTabState(tab.label, { searchKeyword: e.target.value })}
                />
              </div>
              <div style={{ flex: 1, height: 'calc(100% - 20% - 10px)', overflowY: 'auto' }}>
                <Checkbox.Group
                  options={allOptions.map(item => ({
                    label: tab.getLabel(item) || '',
                    value: tab.getLabel(item) || '',
                  }))}
                  value={currentSelected.map(item => tab.getLabel(item))}
                  onChange={(checkedValues: string[]) => {
                    // checkedValues에서 선택된 항목들을 찾기
                    const selected: T[] = [];
                    checkedValues.forEach(value => {
                      // 먼저 options에서 찾기
                      let found = allOptions.find(item => tab.getLabel(item) === value);
                      // 없으면 이미 선택된 항목에서 찾기
                      if (!found) {
                        found = currentSelected.find(item => tab.getLabel(item) === value);
                      }
                      if (found) {
                        selected.push(found);
                      }
                    });
                    // selectedItems를 먼저 업데이트하여 즉시 UI에 반영
                    setSelectedItems(prev => ({
                      ...prev,
                      [tab.label]: selected,
                    }));
                    // 그 다음 외부 상태 업데이트
                    tab.setSelected(selected);
                  }}
                />
              </div>
            </div>
          </div>
        ),
      };
    });
  }, [tabs, tabStates, selectedItems]);

  React.useEffect(() => {
    const currentTab = tabs.find(tab => tab.label === selectedTab);
    if (currentTab) {
      const tabState = tabStates[selectedTab];
      const searchKeyword = tabState?.searchKeyword || '';
      currentTab.fetchSearch(searchKeyword).then(res => {
        setTabStates(prev => ({
          ...prev,
          [selectedTab]: { ...(prev[selectedTab] || { searchKeyword: '' }), options: res },
        }));
      });
    }
  }, [tabStates[selectedTab]?.searchKeyword, selectedTab]);

  React.useEffect(() => {
    const initialState: Record<string, { searchKeyword: string; options: T[] }> = {};
    tabs.forEach(tab => {
      // 초기 로드 시 선택된 항목들을 options에 포함
      const initialOptions = [...tab.selected];
      initialState[tab.label] = { searchKeyword: '', options: initialOptions };
    });
    setTabStates(initialState);

    // 초기 로드 시 각 탭의 옵션을 가져오기
    tabs.forEach(tab => {
      tab.fetchSearch('').then(res => {
        setTabStates(prev => {
          const currentOptions = prev[tab.label]?.options || [];
          // 선택된 항목들을 유지하면서 새로운 검색 결과 추가
          const mergedOptions = [...currentOptions];
          res.forEach(item => {
            const label = tab.getLabel(item);
            const exists = mergedOptions.some(existing => tab.getLabel(existing) === label);
            if (!exists) {
              mergedOptions.push(item);
            }
          });
          return {
            ...prev,
            [tab.label]: { ...(prev[tab.label] || { searchKeyword: '' }), options: mergedOptions },
          };
        });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ height: '100%' }}>
      <Tabs
        defaultActiveKey={selectedTab}
        type="card"
        onChange={setSelectedTab}
        style={{ height: '100%' }}
        items={tabItems}
      />
    </div>
  );
};

export default CustomSelector;
